import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package, Truck, CheckCircle2, FileText, Loader2, Download,
  RefreshCw, AlertTriangle,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Flipkart fulfillment state progression — controls which buttons are
// enabled. Matches backend `order.flipkart_state`.
const STATE_ORDER = [
  'APPROVED',
  'PACKED',
  'READY_TO_DISPATCH',
  'DISPATCHED',
  'DELIVERED',
  'CANCELLED',
];

function stateBadgeColor(state) {
  switch ((state || '').toUpperCase()) {
    case 'APPROVED':
      return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
    case 'PACKED':
      return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'READY_TO_DISPATCH':
      return 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30';
    case 'DISPATCHED':
    case 'SHIPPED':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'DELIVERED':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'CANCELLED':
      return 'bg-red-500/15 text-red-300 border-red-500/30';
    default:
      return 'bg-wt-subtle text-wt-text-muted border-wt-border';
  }
}

function atOrAfter(currentState, target) {
  // Flipkart reports SHIPPED for dispatched self-ship/courier-scanned
  // shipments — same rank as our internal DISPATCHED.
  const norm = (currentState || 'APPROVED').toUpperCase() === 'SHIPPED'
    ? 'DISPATCHED'
    : (currentState || 'APPROVED').toUpperCase();
  const ci = STATE_ORDER.indexOf(norm);
  const ti = STATE_ORDER.indexOf(target);
  return ci >= ti;
}

export default function FlipkartFulfillmentActions({ order, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const state = (order.flipkart_state || 'APPROVED').toUpperCase();
  const isSelfShip = (order.flipkart_shipment_type || '').toUpperCase() === 'SELF';
  const token = localStorage.getItem('token');

  const call = async (action, path, opts = {}) => {
    setBusy(action);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/integrations/flipkart/fulfillment/orders/${order.id}/${path}`,
        opts.body || {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.ok === false) {
        toast.error(`Flipkart rejected: ${JSON.stringify(res.data?.response || {}).slice(0, 160)}`);
      } else {
        toast.success(opts.successMessage || `${action} — done`);
      }
      if (onRefresh) await onRefresh();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  const handlePack = () => call('Pack', 'pack', { successMessage: 'Marked as Packed on Flipkart' });
  const handleRTD = () => call('Ready to Dispatch', 'ready-to-dispatch', {
    successMessage: 'Moved to Ready-to-Dispatch',
  });
  const handleDispatch = () => call('Dispatch', 'dispatch', {
    successMessage: isSelfShip
      ? 'AWB pushed to Flipkart — marked SHIPPED'
      : 'Dispatched — stock committed',
  });
  const handleSelfShipDelivered = () => call('Mark Delivered', 'self-ship-delivered', {
    successMessage: 'Marked DELIVERED on Flipkart',
  });

  const handleDownloadLabel = async () => {
    setBusy('Label');
    try {
      const res = await axios.post(
        `${API_BASE_URL}/integrations/flipkart/fulfillment/orders/${order.id}/download-label`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const labelUrl = res.data?.label_pdf_base64_url || res.data?.pdf_base64_url;
      const invoiceUrl = res.data?.invoice_pdf_base64_url;

      const openPdf = (url, title) => {
        const w = window.open();
        if (w) {
          w.document.write(
            `<title>${title}</title><iframe src="${url}" style="width:100%;height:100%;border:0" title="${title}"></iframe>`
          );
        }
      };

      if (labelUrl) openPdf(labelUrl, 'Flipkart Shipping Label');
      if (invoiceUrl) openPdf(invoiceUrl, 'Flipkart Tax Invoice');

      if (res.data?.split && invoiceUrl) {
        toast.success('Downloaded — shipping label & tax invoice opened in separate tabs');
      } else if (res.data?.split && res.data?.invoice_error) {
        toast.warning('Shipping label downloaded, but invoice fetch failed — retry shortly');
      } else {
        toast.success('Label downloaded');
      }
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Label download failed');
    } finally {
      setBusy(null);
    }
  };

  const btn = (label, onClick, Icon, disabled, testId) => (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={!!busy || disabled}
      className="border-wt-border text-wt-text-primary hover:bg-wt-subtle h-8 text-xs"
      data-testid={testId}
    >
      {busy === label ? (
        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
      ) : (
        <Icon className="w-3.5 h-3.5 mr-1" />
      )}
      {label}
    </Button>
  );

  const isCancelled = state === 'CANCELLED';
  const hasLabel = !!order.flipkart_label_downloaded_at;

  return (
    <div
      className="bg-wt-paper rounded-lg border border-wt-border p-4 space-y-3"
      data-testid="flipkart-fulfillment-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-wt-gold" />
          <span className="text-sm font-medium text-wt-text-primary">Flipkart Fulfillment</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-xs ${stateBadgeColor(state)}`}
            data-testid="flipkart-state-badge"
          >
            {state.replace(/_/g, ' ')}
          </Badge>
          {isSelfShip && (
            <Badge
              variant="outline"
              className="text-xs bg-purple-500/15 text-purple-300 border-purple-500/30"
              data-testid="flipkart-self-ship-badge"
            >
              Self Ship
            </Badge>
          )}
        </div>
      </div>

      {isSelfShip && (
        <p className="text-xs text-wt-text-muted flex items-start gap-1.5" data-testid="flipkart-self-ship-note">
          <Truck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Self-ship order — book a courier in Opswarex as usual; the AWB is pushed
          to Flipkart automatically. "Dispatch" retries the push if needed.
        </p>
      )}

      {order.on_hold_reason === 'unmapped_channel_sku' && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">On hold — unmapped SKUs</p>
            <p className="text-yellow-200/70 mt-0.5">
              Map these on the Channel Listings page before packing:{' '}
              <span className="font-mono">
                {(order.on_hold_channel_skus || []).join(', ')}
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {btn(
          'Pack',
          handlePack,
          Package,
          isCancelled || atOrAfter(state, 'PACKED'),
          'flipkart-pack-btn'
        )}
        {btn(
          'Ready to Dispatch',
          handleRTD,
          CheckCircle2,
          isCancelled || !atOrAfter(state, 'PACKED') || atOrAfter(state, 'READY_TO_DISPATCH'),
          'flipkart-rtd-btn'
        )}
        {btn(
          'Label',
          handleDownloadLabel,
          hasLabel ? Download : FileText,
          isCancelled || !atOrAfter(state, 'READY_TO_DISPATCH'),
          'flipkart-label-btn'
        )}
        {btn(
          'Dispatch',
          handleDispatch,
          Truck,
          isCancelled
            || !atOrAfter(state, isSelfShip ? 'PACKED' : 'READY_TO_DISPATCH')
            || atOrAfter(state, 'DISPATCHED'),
          'flipkart-dispatch-btn'
        )}
        {isSelfShip && btn(
          'Mark Delivered',
          handleSelfShipDelivered,
          CheckCircle2,
          isCancelled || !atOrAfter(state, 'DISPATCHED') || atOrAfter(state, 'DELIVERED'),
          'flipkart-self-ship-delivered-btn'
        )}
      </div>

      {hasLabel && (
        <p className="text-xs text-wt-text-muted flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Last label downloaded{' '}
          {new Date(order.flipkart_label_downloaded_at).toLocaleString()}
        </p>
      )}
      {order.awb_number && (
        <p className="text-xs text-wt-text-muted">
          AWB: <span className="font-mono text-wt-text-primary">{order.awb_number}</span>
        </p>
      )}
    </div>
  );
}
